import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { closeModalAction } from '../../../actions/modal';
import { addNewPincode } from '../../../actions/admin';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import Select from '../../../common/Form/Select';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import { useForm } from 'react-hook-form';
import { actualValueForDropdown } from '../../../utils/common';
import apis from '../../../apis';
import scogo_apis from '../../../scogo_apis';

const formName = 'add_new_pincode';
const CITY_PAGE_SIZE = 100;

export default function AddNewPincode({ pincode, onSuccess = () => { }, onAddAndContinue, addAndContinueText = 'Add & Create Ticket' }) {
    // When opened from a create flow (ticket/site), the parent passes onAddAndContinue so we offer a second action.
    const showContinueButton = typeof onAddAndContinue === 'function';
    const defaultValues = {
        pincode: pincode ? pincode : '',
        city: null,
    };
    const { formButtonLoading } = useSelector((state) => state.loading);
    const isLoading = formButtonLoading[formName];
    const dispatch = useDispatch();

    const [cityOptions, setCityOptions] = useState([]);
    const [isCityLoading, setIsCityLoading] = useState(false);

    // Tracks which button triggered the submit so we know whether to also create the ticket.
    const createTicketAfterAddRef = useRef(false);
    const submitRef = useRef();

    // Fetch up to 100 cities at a time; `query` narrows the list server-side.
    const fetchCities = useCallback(async (query = '') => {
        setIsCityLoading(true);
        try {
            const queryParams = {
                limit: CITY_PAGE_SIZE,
                page: 1,
                key: 'city',
                sort_by: 'created_at',
                sort_order: 'desc',
            };
            if (query) queryParams.q = query;
            const response = await scogo_apis.admin.getRegionsData({}, {}, queryParams);
            if (response.status === 200 && Array.isArray(response.data?.data)) {
                setCityOptions(response.data.data.map((region) => ({ value: region.id, label: region.name, id: region.id })));
            } else {
                setCityOptions([]);
            }
        } catch (err) {
            setCityOptions([]);
        } finally {
            setIsCityLoading(false);
        }
    }, []);

    const debouncedFetchCities = useCallback(debounce((query) => fetchCities(query), 300), [fetchCities]);

    useEffect(() => {
        fetchCities('');
    }, [fetchCities]);

    const triggerSubmit = (createTicket) => {
        createTicketAfterAddRef.current = createTicket;
        submitRef.current?.click();
    };

    const submit = (formValues) => {
        const successCb = createTicketAfterAddRef.current && showContinueButton ? onAddAndContinue : onSuccess;
        dispatch(addNewPincode({
            data: {
                pincode: formValues.pincode,
                container_region_id: actualValueForDropdown(formValues.city),
            },
            formName,
            onSuccess: successCb,
        }));
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            formName={formName}
            submitRef={submitRef}
        >
            <div className='px-2 py-2 flex'>
                <Select
                    name='city'
                    label='City'
                    required
                    options={cityOptions}
                    isLoading={isCityLoading}
                    onInputChange={(value, meta) => {
                        if (meta?.action === 'input-change') debouncedFetchCities(value);
                    }}
                    className='w-6/12'
                />
                <Input
                    label='Pincode'
                    required
                    name='pincode'
                    type={'number'}
                    pattern={/^\d{6}$/}
                    validate={async (value) => {
                        if (value === '') return true;
                        const response = await apis.cities.getCityFromPincode({}, {}, {}, { pincode: value });
                        return response.data?.code !== 200 || 'Pincode already exist in the System.';
                    }}
                    className='w-6/12'
                />
            </div>
            <div className='flex gap-2 justify-end py-4 px-4'>
                <ButtonScogoClosedOutlined textOrComponent='Cancel' type='button' onClick={() => dispatch(closeModalAction())} />
                <ButtonScogoPrimary textOrComponent='Add' type='button' loading={isLoading} onClick={() => triggerSubmit(false)} />
                {showContinueButton && (
                    <ButtonScogoPrimary textOrComponent={addAndContinueText} type='button' loading={isLoading} onClick={() => triggerSubmit(true)} />
                )}
            </div>
        </Form>
    );
}
